/**
 * UpgradeModal (iter52)
 *
 * Modal global activado por el contexto de suscripción cuando el usuario
 * intenta usar una feature bloqueada. Muestra propuestas de upgrade con
 * CTA hacia /suscripcion.
 */
import { useNavigate } from "react-router-dom";
import { useSubscription } from "../context/SubscriptionContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, ArrowUpRight, Check } from "lucide-react";
import { IS_LOCAL_EDITION } from "../lib/edition";

const FEATURE_BENEFITS = {
  pdf_reader: [
    "Lectura automática (OCR) de PDF de facturas y CCF",
    "Extracción de NIT, total, IVA y fecha sin teclear",
    "Hasta 90% menos tiempo en captura",
  ],
  iva_auto: ["Cálculo automático del IVA por compra y venta",
              "Libros de IVA siempre actualizados al instante"],
  f14: ["Generación automática del F-14 oficial",
         "Listo para subir al portal de Hacienda"],
  anexos: ["Anexos IVA (ventas, compras, sujetos excluidos) en CSV listos para MH"],
  auto_purchase_classification: ["Clasificación automática de compras por cuenta contable"],
  accounting_basic: ["Asientos contables automáticos por cada DTE"],
  accounting_full: ["Plan de cuentas personalizado",
                     "Mayor + Diario + Estados financieros mensuales"],
  multi_company: ["Hasta 3 empresas en una sola cuenta", "Cambio rápido entre clientes"],
  period_close: ["Cierre de período con snapshot inmutable",
                  "Reapertura controlada con auditoría"],
  audit_logs: ["Historial completo de cambios y emisiones",
                "Trazabilidad de cada acción del equipo"],
  backup: ["Backup mensual completo (ZIP) descargable"],
  export_full: ["Exportación de Diario, Mayor, F-14 en Excel y PDF"],
  dashboard: ["Tablero ejecutivo con KPIs en tiempo real"],
  bulk_exports: ["Exporta todos los clientes a la vez"],
  accountant_panel: ["Panel especializado para contadores con vista por cliente"],
  inventory: ["Catálogo de productos y servicios", "Existencias, entradas y salidas", "Valorización y reportes de inventario"],
};

const FEATURE_TITLES = {
  pdf_reader: "Lectura de PDF (OCR)",
  iva_auto: "IVA automático",
  f14: "Formulario F-14",
  anexos: "Anexos IVA",
  auto_purchase_classification: "Clasificación automática de compras",
  accounting_basic: "Contabilidad automática",
  accounting_full: "Contabilidad completa",
  multi_company: "Multi-empresa",
  period_close: "Cierre de período",
  audit_logs: "Auditoría",
  backup: "Backup mensual",
  export_full: "Exportaciones avanzadas",
  dashboard: "Dashboard ejecutivo",
  bulk_exports: "Exportaciones masivas",
  accountant_panel: "Panel de contador",
  inventory: "Inventario y existencias",
};

export default function UpgradeModal() {
  const { upgradeFor, setUpgradeFor } = useSubscription();
  const nav = useNavigate();
  const open = !!upgradeFor;

  if (IS_LOCAL_EDITION) return null;
  if (!open) return null;

  const fname = upgradeFor.feature;
  const title = FEATURE_TITLES[fname] || "Esta función";
  const benefits = FEATURE_BENEFITS[fname] || [
    "Acceso completo a esta funcionalidad",
    "Mejora tu productividad como contador o emisor",
  ];
  const reqPlan = upgradeFor.requiredPlan || "Pro";

  const goPlans = () => {
    setUpgradeFor(null);
    nav("/suscripcion");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setUpgradeFor(null); }}>
      <DialogContent className="sm:max-w-md" data-testid="upgrade-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 text-amber-600">
            <Sparkles className="w-5 h-5" strokeWidth={2} />
            <span className="text-xs uppercase tracking-wider font-semibold">
              Disponible en {reqPlan}
            </span>
          </div>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          <DialogDescription className="text-sm text-slate-700 leading-relaxed">
            Estás en el plan <strong>{upgradeFor.currentPlan}</strong>. Para
            usar esta funcionalidad mejora a <strong>{reqPlan}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2" data-testid="upgrade-benefits">
          <div className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">
            Lo que obtienes con {reqPlan}
          </div>
          <ul className="space-y-2 text-sm text-slate-800">
            {benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" strokeWidth={2.5} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => setUpgradeFor(null)}
            data-testid="upgrade-cancel"
          >
            Quizás luego
          </Button>
          <Button
            onClick={goPlans}
            data-testid="upgrade-cta"
            className="gap-1"
          >
            Ver planes
            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
